/**
 * Purpose: Provides context menu functionality for message bubbles
 *
 * How it works:
 * 1. Renders a context menu with reaction options and action buttons
 * 2. Handles different states based on message status and permissions
 * 3. Directly accesses global state and actions without prop drilling
 * 4. Manages all message interactions internally
 *
 * Key features:
 * - Self-contained component with direct state access
 * - Reaction emoji selection
 * - Message actions (reply, copy, edit, delete)
 * - Permission-based action visibility
 * - Mobile/desktop support
 */

import { MessageEntity, MessageSendingStatus } from "@/indexedDB/types";
import {
  ContextMenuContent,
  ContextMenuItem,
} from "@/components/primitives/context-menu";
import {
  Copy,
  Forward,
  MessageSquareReply,
  MessageSquareQuote,
  Pencil,
  Pin,
  PinOff,
  Trash2,
} from "lucide-react";
import { IS_MOBILE } from "@/lib/environmentDetector";
import { ALLOWED_REACTIONS, MESSAGE_EDIT_TTL } from "@/config/constants";
import { useTranslation } from "react-i18next";
import { MemberRole } from "@/server/types/member";
import { ChatType } from "@/server/types/chat";
import { useNavigationState } from "@/zustand/NavigationState";
import { useRtfInputState } from "@/zustand/RtfInputState";
import { useMessageActions } from "./hooks/useMessageActions";
import { cn, timeDiffFromNow } from "@/lib/utils";
import { umbra_removeReactionFromMessage } from "@/middleware/umbra_reactions";
import { toast } from "@/hooks/useToast";
import { useForwardMessageState } from "@/zustand/ForwardMessageState";

interface Props {
  message: MessageEntity;
}

export default function MessageContextMenu({ message }: Props) {
  const { t } = useTranslation();
  const msgTTL = timeDiffFromNow(message.timestamp, "minutes");

  // Access navigation state directly
  const activeChatType = useNavigationState((state) => state.activeChatType);
  const activeThreadId = useNavigationState((state) => state.activeThreadId);
  const activeChatAllowThreads = useNavigationState(
    (state) => state.activeChatAllowThreads,
  );
  const activeChatCanPost = useNavigationState(
    (state) => state.activeChatCanPost,
  );
  const setActiveThreadId = useNavigationState(
    (state) => state.setActiveThreadId,
  );
  const activeChatRoleAsMember = useNavigationState(
    (state) => state.activeChatRoleAsMember,
  );

  // Access message action handlers directly
  const {
    handleReactToMessage,
    handleDeleteMessage,
    handleDeleteMessageFromLocalDB,
    handleCopyTextToClipboard,
    handleTogglePinMessage,
  } = useMessageActions();

  // RTF input state for editing and replying
  const setEditMessage = useRtfInputState((state) => state.setEditMessage);
  const clearEditMessage = useRtfInputState((state) => state.clearEditMessage);
  const setReplyMessage = useRtfInputState((state) => state.setReplyMessage);
  const clearReplyMessage = useRtfInputState(
    (state) => state.clearReplyMessage,
  );
  const clearDraftMessage = useRtfInputState(
    (state) => state.clearDraftMessage,
  );

  // Message action handlers
  const handleReply = () => {
    setReplyMessage(message.chatId, message);
    clearEditMessage(message.chatId);
    clearDraftMessage(message.chatId);
  };

  const handleNavigateToThreadComments = () => {
    // extra prevent reply in thread loop
    if (activeThreadId) {
      return;
    }

    // set active thread message id
    setActiveThreadId(message.id);
  };

  const handleEdit = () => {
    setEditMessage(message.chatId, message);
    clearReplyMessage(message.chatId);
    clearDraftMessage(message.chatId);
  };

  const isAtLeastAdmin = activeChatRoleAsMember <= MemberRole.ADMIN;
  const isAtLeastModerator = activeChatRoleAsMember <= MemberRole.MODERATOR;

  // Check if user is admin or owner in a group chat
  const isAdminOrOwnerInGroup =
    activeChatType === ChatType.GROUP && isAtLeastAdmin;

  // A message is actionable once it has a confirmed server identity. The
  // distinction that matters is "server-acked vs local-optimistic", NOT the
  // delivery/read-receipt state: SENT/DELIVERED/READ all carry a server id, so
  // gate only against the local-only states (PENDING = not yet acked, FAILED =
  // never acked). Gating on DELIVERED/READ alone wrongly hid every action but
  // Copy for SENT messages — e.g. sync/backfill rows are written as SENT and a
  // bulkPut can downgrade a previously READ row back to SENT.
  const isMessageStatusOk =
    message.sendingStatus !== MessageSendingStatus.PENDING &&
    message.sendingStatus !== MessageSendingStatus.FAILED;

  const editIsWithinTTL = msgTTL !== null && msgTTL < MESSAGE_EDIT_TTL;

  // Message actions RULES AND VISIBILITY
  const canReact = isMessageStatusOk;

  const canPin =
    isMessageStatusOk &&
    !activeThreadId && // disable pinning in thread view
    (isAtLeastModerator || activeChatType === ChatType.P2P);

  const canReply =
    isMessageStatusOk &&
    !activeThreadId &&
    activeChatRoleAsMember <= activeChatCanPost;

  const canReplyInThread =
    isMessageStatusOk && activeThreadId && activeChatAllowThreads;

  const canSeeThreadComments =
    isMessageStatusOk &&
    activeChatType === ChatType.GROUP &&
    activeChatAllowThreads &&
    !activeThreadId;

  const canEdit =
    message.isOutgoing &&
    isMessageStatusOk &&
    editIsWithinTTL &&
    (message.content.text || message.content.media || message.content.file);

  const canCopy = !!message.content.text;

  const canDelete =
    isMessageStatusOk && (message.isOutgoing || isAdminOrOwnerInGroup);

  // A confirmed FAILED send is a local-only, safely-deletable row. A row that is
  // FAILED-but-`deliveryUnconfirmed` may actually be on the server (its send
  // committed but the response died — #1177), so it must NOT offer a local delete
  // that would silently drop a delivered message. It renders "Not confirmed sent"
  // and offers Copy only.
  const canDeleteFailedMessages =
    message.isOutgoing &&
    message.sendingStatus === MessageSendingStatus.FAILED &&
    !message.deliveryUnconfirmed;

  // SENT ≠ bytes-complete for chunked uploads: the message row is posted
  // FIRST (watch-while-uploading), so the status gate alone would allow
  // forwarding while the attachment bytes are still uploading — which is
  // guaranteed to fail (the server has no assembled object yet). "failed"
  // is blocked too, intentionally: that content never assembled. Absent
  // uploadState means a plain single-object upload, complete by the time
  // the message exists.
  const uploadComplete =
    !message.content.media?.uploadState ||
    message.content.media.uploadState.status === "complete";

  const canForward = isMessageStatusOk && uploadComplete;

  const openForwardModal = useForwardMessageState(
    (state) => state.openForwardModal,
  );
  const isForwarding = useForwardMessageState((state) => state.isForwarding);

  const userReacted = !!message.userReaction && !!message.userReaction.content;

  const handleRemoveReaction = async () => {
    if (!message.userReaction?.content) {
      return;
    }

    try {
      await umbra_removeReactionFromMessage({
        chatId: message.chatId,
        messageId: message.id,
        emoji: message.userReaction?.content,
      });
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : t("errorOccurred"),
        variant: "destructive",
      });
    }
  };

  // prevent context menu from showing for pending messages
  if (message.sendingStatus === MessageSendingStatus.PENDING) {
    return null;
  }

  return (
    <ContextMenuContent
      collisionBoundary={document.body}
      alignOffset={10}
      collisionPadding={10}
      sticky="always"
      backdrop={IS_MOBILE ? "blur" : "transparent"}
      className="flex flex-col gap-2 border-none bg-transparent shadow-none outline-none drop-shadow-md"
    >
      {canReact && (
        <div className="custom-scroll custom-scroll-x flex max-w-[var(--radix-context-menu-content-available-width)] flex-row items-center gap-0 overflow-auto rounded-lg bg-card p-2 align-middle">
          {!userReacted ? (
            ALLOWED_REACTIONS.map((reaction) => (
              <ContextMenuItem
                key={reaction}
                onClick={() => handleReactToMessage(message, reaction)}
                className="lg:cursor-pointer"
              >
                {reaction}
              </ContextMenuItem>
            ))
          ) : (
            <div
              className="flex items-center gap-3 lg:cursor-pointer"
              onClick={userReacted ? handleRemoveReaction : undefined}
            >
              <div className="text-secondary/80">Tap to remove</div>
              <div className="size-5 shrink-0">
                {message.userReaction?.content}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex min-w-32 flex-col gap-0 self-end rounded-lg bg-card p-2">
        {canSeeThreadComments && (
          <ContextMenuItem
            onClick={handleNavigateToThreadComments}
            className="flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer"
          >
            <MessageSquareQuote className="size-4" />
            {t("threads.comment")}
          </ContextMenuItem>
        )}

        {canReply && (
          <ContextMenuItem
            onClick={handleReply}
            className="flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer"
          >
            <MessageSquareReply className="size-4" />
            {t("reply")}
          </ContextMenuItem>
        )}

        {canReplyInThread && (
          <ContextMenuItem
            onClick={handleReply}
            className="flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer"
          >
            <MessageSquareReply className="size-4" />
            {t("reply")}
          </ContextMenuItem>
        )}

        {canForward && (
          <ContextMenuItem
            onClick={() => !isForwarding && openForwardModal(message)}
            className={cn(
              "flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer",
              isForwarding && "pointer-events-none opacity-50",
            )}
          >
            <Forward className="size-4" />
            {t("forward")}
          </ContextMenuItem>
        )}

        {canCopy && (
          <ContextMenuItem
            onClick={() => handleCopyTextToClipboard(message.content.text)}
            className="flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer"
          >
            <Copy className="size-4" />
            {t("copy")}
          </ContextMenuItem>
        )}

        {canPin && (
          <ContextMenuItem
            onClick={() => handleTogglePinMessage(message)}
            className="flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer"
          >
            {message.isPinned ? (
              <PinOff className="size-4" />
            ) : (
              <Pin className="size-4" />
            )}
            {t(message.isPinned ? "unpin" : "pin")}
          </ContextMenuItem>
        )}

        {/* Show edit option only for outgoing messages */}
        {canEdit && (
          <ContextMenuItem
            onClick={handleEdit}
            className="flex flex-row items-center gap-2 p-2 align-middle lg:cursor-pointer"
          >
            <Pencil className="size-4" />
            {t("edit")}
          </ContextMenuItem>
        )}

        {/* Show delete option for outgoing messages or when user is admin/owner in a group chat */}
        {canDelete && (
          <ContextMenuItem
            onClick={() => handleDeleteMessage(message.id)}
            className="flex flex-row items-center gap-2 p-2 align-middle text-destructive lg:cursor-pointer"
          >
            <Trash2 className="size-4" />
            {t("delete")}
          </ContextMenuItem>
        )}

        {/* Handle failed messages */}
        {canDeleteFailedMessages && (
          <ContextMenuItem
            onClick={() => handleDeleteMessageFromLocalDB(message.id)}
            className="flex flex-row items-center gap-2 p-2 align-middle text-destructive lg:cursor-pointer"
          >
            <Trash2 className="size-4" />
            {t("delete")}
          </ContextMenuItem>
        )}
      </div>
    </ContextMenuContent>
  );
}
